// src/components/About.js
// "Meet your coach" section on the homepage scroll (anchor: #about). Folds the
// old standalone About page in: rotating portrait + full bio + coaching
// philosophy, on the shared section-heading design language.
import React, { useState, useEffect } from "react";
import "./About.css";

import andrew2 from "../assets/images/solo-photos/Andrew_Saillant-2.jpg";
import andrew7 from "../assets/images/solo-photos/Andrew_Saillant-7.jpg";
import andrew18 from "../assets/images/solo-photos/Andrew_Saillant-18.jpg";
import andrew31 from "../assets/images/solo-photos/Andrew_Saillant-31.jpg";
import img3975 from "../assets/images/solo-photos/IMG_3975.JPG";
import img3986 from "../assets/images/solo-photos/IMG_3986.JPG";
import img3995 from "../assets/images/solo-photos/IMG_3995.JPG";
import soloReffing from "../assets/images/solo-photos/Solo Reffing.jpg";

const soloPhotos = [
  andrew2,
  andrew7,
  andrew18,
  andrew31,
  img3975,
  img3986,
  img3995,
  soloReffing,
];

// Principles, not deliverables. The "what you get" (programming, daily
// communication, meet handling) lives in the Services section; this is the "why"
// behind it, so the two don't restate each other.
// Scannable credentials so the prose can stay short.
const facts = ["Est. 2022", "Rhode Island", "CSCS Certified", "DPT Student"];

const pillars = [
  {
    title: "Individualized by principle",
    body: 'No template, no dogma. The right program is the one that fits your anatomy, history, and goals, and that changes as you do. I follow what the data and the athlete in front of me actually show.',
  },
  {
    title: "Invested, not transactional",
    body: "I want to know the person behind the lifts. The relationships I build with my athletes drive both my investment in their success and their own buy-in. You are never just a name in a spreadsheet.",
  },
];

const About = () => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Auto-rotate the portrait, but hold still for reduced-motion users.
  useEffect(() => {
    if (soloPhotos.length <= 1) return undefined;
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    const interval = setInterval(() => {
      setCurrentPhotoIndex((prev) => (prev + 1) % soloPhotos.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="about" className="home-section about">
      <div className="section-heading">
        <p className="section-heading__eyebrow">Meet your coach</p>
        <h2>Andrew Saillant</h2>
        <p>
          Founder and head coach of SSS Powerlifting, building toward
          world-leading, powerlifting-specific coaching.
        </p>
      </div>

      <ul className="about__facts" aria-label="Coach credentials">
        {facts.map((fact) => (
          <li key={fact} className="about__fact">
            {fact}
          </li>
        ))}
      </ul>

      <div className="about__main">
        <div className="about__media">
          <img
            src={soloPhotos[currentPhotoIndex]}
            alt="Coach Andrew Saillant"
            className="about__photo"
          />
          {soloPhotos.length > 1 && (
            <div className="about__indicators">
              {soloPhotos.map((photo, index) => (
                <button
                  key={`about-photo-${index}`}
                  type="button"
                  className={`about__indicator ${
                    index === currentPhotoIndex ? "is-active" : ""
                  }`}
                  onClick={() => setCurrentPhotoIndex(index)}
                  aria-label={`View photo ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="about__bio">
          <p>
            I'm Andrew Saillant, founder of Saillant Strength Systems
            Powerlifting, established in 2022 and based in Rhode Island. I
            started lifting in 2017 and competing in powerlifting in 2019, and
            found coaching soon after as a trainer at Boston College, where it
            pulled me off a physics track and onto the path to physical therapy.
          </p>
          <p>
            Through college I coached dozens of clients and grew the business by
            word of mouth out of Cambridge Strength and Conditioning, founding
            the Boston College Barbell Club along the way. After graduating in
            2023 I went all in on coaching and earned my CSCS. Now I'm a Doctor
            of Physical Therapy student at the University of Rhode Island,
            chasing one goal: to be the powerlifting-specific physical therapist
            and coach I always needed.
          </p>
        </div>
      </div>

      <div className="about__philosophy">
        <h3 className="about__philosophy-title">Coaching philosophy</h3>
        <p className="about__philosophy-lead">
          I take my job seriously and never compromise my own effort or standards
          of service, and I expect athletes who work with me to hold themselves
          to that same standard in training and communication. At the same time,
          I value building real relationships with my athletes, going out of my
          way to talk about their lives, shared interests, and current events.
          That investment deepens my commitment to each athlete's success and
          builds buy-in by making them more than a name in a spreadsheet.
        </p>

        <ol className="about__pillars">
          {pillars.map((pillar, index) => (
            <li key={pillar.title} className="about__pillar">
              <span className="about__pillar-num">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h4 className="about__pillar-title">{pillar.title}</h4>
              <p className="about__pillar-body">{pillar.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default About;
